/**
 * Admin — Positions & permissions list. Statutory positions (Chairman,
 * Hon. Secretary, Hon. Treasurer, Committee Member, Expert Director) ship
 * locked to the Maharashtra model bye-laws; societies add custom positions
 * below them. Wired: GET /api/positions. Editor at /admin/positions/[id].
 */
import { router } from 'expo-router';
import React from 'react';
import { View } from 'react-native';

import { space } from '@bero/tokens';
import { bool, num, str, useGateList } from '@/components/gate/api';
import { ModerationScreen } from '@/features/account-admin/bits';
import { Icon } from '@/features/account-admin/icons';
import { useTheme } from '@/theme';
import { AppText, Card, Pill } from '@/ui';

type Row = {
  id: string; name: string; description: string;
  isTemplate: boolean; lockedCount: number; memberCount: number;
};

function toRow(v: unknown, i: number): Row {
  return {
    id: str(v, 'id') ?? `p${i}`,
    name: str(v, 'name') ?? 'Position',
    description: str(v, 'description') ?? '',
    isTemplate: bool(v, 'isTemplate') ?? false,
    lockedCount: num(v, 'lockedCount') ?? 0,
    memberCount: num(v, 'memberCount') ?? 0,
  };
}

export default function AdminPositionsScreen() {
  const { colors } = useTheme();
  const { items, state } = useGateList<unknown>('/api/positions', 'positions');
  const rows = items.map(toRow);

  return (
    <ModerationScreen
      title="Positions"
      createLabel="New position"
      onCreate={() => router.push('/admin/positions/new')}
      state={state}
      count={rows.length}
      emptyIcon="key"
      emptyTitle="No positions yet"
      emptyBody="Positions bundle permissions — who may publish, approve and manage what. Statutory positions appear once this screen loads."
    >
      <AppText variant="bodySm" tone="tertiary" style={{ marginBottom: space.s3 }}>
        Grants marked with a lock come from the model bye-laws and can&apos;t be unticked —
        each one cites the bye-law that mandates it.
      </AppText>
      {rows.map((p) => (
        <Card key={p.id} onPress={() => router.push(`/admin/positions/${p.id}`)}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: space.s3 }}>
            <View style={{
              width: 40, height: 40, borderRadius: 12, backgroundColor: colors.primary100,
              alignItems: 'center', justifyContent: 'center',
            }}>
              <Icon name={p.isTemplate ? 'shield' : 'key'} size={19} color={colors.primary700} />
            </View>
            <View style={{ flex: 1, minWidth: 0 }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: space.s2 }}>
                <AppText variant="bodyMd" weight="600">{p.name}</AppText>
                {p.isTemplate ? <Pill status="approved" label="BYE-LAW" /> : null}
              </View>
              <AppText variant="bodySm" tone="tertiary" style={{ marginTop: 2 }} numberOfLines={1}>
                {p.memberCount === 1 ? '1 holder' : `${p.memberCount} holders`}
                {p.lockedCount > 0 ? ` · ${p.lockedCount} grants locked by law` : ''}
              </AppText>
            </View>
            <Icon name="chevron-right" size={18} color={colors.textTertiary} />
          </View>
        </Card>
      ))}
    </ModerationScreen>
  );
}
